import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api, API_URL } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { formatPrice } from "@/lib/format";
import { CheckCircle2, Download, Sparkles } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [order, setOrder] = useState(null);
  const [polls, setPolls] = useState(0);
  const [done, setDone] = useState(false);
  const timer = useRef(null);
  const { clear } = useCart();

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const check = async () => {
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setOrder(r.data);
        if (r.data?.payment_status === "paid") {
          setDone(true);
          clear();
          return;
        }
      } catch {}
      if (!cancelled) {
        setPolls((p) => p + 1);
        timer.current = setTimeout(check, 2000);
      }
    };
    check();
    return () => { cancelled = true; if (timer.current) clearTimeout(timer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen flex flex-col bg-cream" data-testid="payment-success-page">
      <Header />
      <section className="max-w-3xl mx-auto px-5 lg:px-10 py-20 w-full">
        {!done ? (
          <div className="text-center py-16" data-testid="payment-checking">
            <Sparkles className="h-8 w-8 mx-auto text-gold twinkle" />
            <div className="font-serif-display text-3xl mt-4 text-plum-deep">Finalizing your order…</div>
            <p className="text-plum/70 mt-3">Hang tight, we're confirming the payment with Stripe.</p>
            <div className="mt-6 text-xs text-plum/50">Attempt {polls + 1}</div>
          </div>
        ) : (
          <div data-testid="payment-success-content">
            <div className="inline-flex items-center gap-2 text-gold bg-gold-pale border border-gold/60 rounded-full px-4 py-1 text-xs">
              <CheckCircle2 className="h-4 w-4" /> Payment confirmed
            </div>
            <h1 className="editorial font-serif-display text-4xl sm:text-5xl mt-6 text-plum-deep">Thank you — your order is ready</h1>
            <p className="text-plum/70 mt-3">
              Order <span className="font-medium text-plum-deep">{order?.order_id}</span> · {formatPrice((order?.amount_total || 0) / 100, order?.currency || "usd")}
              <br />A receipt and your download links have been sent to <span className="font-medium">{order?.email}</span>.
            </p>

            <div className="mt-10 border border-brand rounded-sm bg-white">
              <div className="px-6 py-4 border-b border-brand font-medium text-plum-deep">Your downloads</div>
              <ul>
                {(order?.download_tokens || []).length === 0 ? (
                  <li className="px-6 py-10 text-sm text-plum/60" data-testid="no-downloads">
                    This order doesn't have any digital files attached yet. The shop owner will reach out via email with next steps.
                  </li>
                ) : (
                  order.download_tokens.map((t) => (
                    <li key={t.token} className="px-6 py-4 flex items-center justify-between border-b border-brand last:border-b-0 flex-wrap gap-3" data-testid={`download-row-${t.product_id}`}>
                      <div>
                        <div className="font-medium text-plum-deep">{t.title}</div>
                        <div className="text-xs text-plum/60">{t.file_name} · {t.downloads_remaining} downloads left</div>
                      </div>
                      <a
                        href={`${API_URL}/download/${t.token}`}
                        className="btn-primary h-10 px-4 text-sm"
                        data-testid={`download-btn-${t.product_id}`}
                      >
                        <Download className="h-4 w-4" /> Download
                      </a>
                    </li>
                  ))
                )}
              </ul>
            </div>

            <div className="mt-10 text-sm">
              <Link to="/shop" className="underline text-plum-deep" data-testid="back-to-shop">Keep browsing the shop →</Link>
            </div>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
